// Variant A's paywall — the Golden Path trial offering (Figma 19 / 19.2).
//
// Presentation only: the page above owns the membership check, the offer fetch
// and the checkout call, so both arms of the A/B test share identical plumbing
// and differ only in what the visitor sees.
//
// Every number here comes from the offer response. Figma's "$8.25 /Monthly, paid
// annually ($99)" is a US annual plan, not a constant — hardcoding it would show
// USD prices to a visitor being charged CAD.

import React, { useState } from "react";
import styled from "styled-components";
import i18next from "i18next";
import { IPaywallOfferDto, IPaywallServiceDto } from "../../../shared/api/submission";
import { formatMoney } from "./money";
import { MEMBERSHIP_BLUE, membershipPrimary } from "./membershipButton";

interface Props {
  offer: IPaywallOfferDto;
  selectedServiceId: number | null;
  onSelectService: (serviceId: number) => void;
  onCheckout: () => void;
  onIgnore: () => void;
  busy?: boolean;
}

const PaywallTrialOffer: React.FC<Props> = ({
  offer,
  selectedServiceId,
  onSelectService,
  onCheckout,
  onIgnore,
  busy,
}) => {
  const [expanded, setExpanded] = useState(false);

  const annual = offer.services.filter((s) => s.durationMonths >= 12)[0];
  const monthly = offer.services.filter((s) => s.durationMonths < 12)[0];
  const selected: IPaywallServiceDto =
    offer.services.filter((s) => s.serviceId === selectedServiceId)[0] ||
    offer.services.filter((s) => s.serviceId === offer.defaultServiceId)[0] ||
    offer.services[0];

  // What Annual saves against paying monthly for a year — the only reason to
  // pick it, so it belongs on the control rather than in the small print.
  const annualSaving =
    annual && monthly ? Math.round(monthly.price * 12 - annual.price) : 0;

  const benefits = expanded
    ? offer.benefits
    : offer.benefits.slice(0, offer.collapsedBenefitCount);

  return (
    <Wrap>
      <Heading>{i18next.t("Start your free trial now")}</Heading>

      {offer.services.length > 1 && (
        <Toggle role="radiogroup" aria-label={i18next.t("Billing period")}>
          {[annual, monthly].filter(Boolean).map((service) => {
            const isSelected = service.serviceId === selected.serviceId;
            const isAnnual = service.durationMonths >= 12;
            return (
              <ToggleOption
                key={service.serviceId}
                type="button"
                role="radio"
                aria-checked={isSelected}
                selected={isSelected}
                onClick={() => onSelectService(service.serviceId)}
              >
                {isAnnual ? i18next.t("Annual") : i18next.t("Monthly")}
                {isAnnual && annualSaving > 0 && (
                  <Saving>
                    {i18next.t("save {{amount}}", {
                      amount: formatMoney(annualSaving, service, { whole: true }),
                    })}
                  </Saving>
                )}
              </ToggleOption>
            );
          })}
        </Toggle>
      )}

      <PlanCard>
        <PlanName>{i18next.t("PRO")}</PlanName>
        <AfterTrial>
          {i18next.t("After {{count}} days", { count: selected.trialDurationDays })}
        </AfterTrial>
        {/* aria-live: the price changes under the visitor when they toggle, and a
            screen reader has no other way to notice. */}
        <Price aria-live="polite">
          {formatMoney(selected.monthlyEquivalent, selected)}
          <PriceSuffix>
            {selected.durationMonths >= 12
              ? i18next.t("/Monthly, paid annually ({{total}})", {
                  total: formatMoney(selected.price, selected),
                })
              : i18next.t("/Monthly")}
          </PriceSuffix>
        </Price>

        <Benefits>
          {benefits.map((benefit) => (
            <Benefit key={benefit}>
              <TickIcon />
              <span>{benefit}</span>
            </Benefit>
          ))}
        </Benefits>

        {offer.benefits.length > offer.collapsedBenefitCount && (
          <ExpandButton
            type="button"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
          >
            {expanded ? i18next.t("Hide all benefits") : i18next.t("See all benefits")}
          </ExpandButton>
        )}

        {offer.ccRequired && (
          <CcBadge>
            <CardIcon />
            {i18next.t("CC Required - Cancel Anytime!")}
          </CcBadge>
        )}
      </PlanCard>

      <PrimaryButton onClick={onCheckout} disabled={busy}>
        {busy ? i18next.t("One moment…") : i18next.t("Continue")}
      </PrimaryButton>
      <TextButton type="button" onClick={onIgnore} disabled={busy}>
        {i18next.t("Ignore trial")}
      </TextButton>
    </Wrap>
  );
};

const Wrap = styled.div`
  padding: 8px 0 4px;
`;

const TickIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    aria-hidden="true"
    style={{ flex: "none", marginTop: 2, marginRight: 8 }}
  >
    <circle cx="8" cy="8" r="8" fill="#16a34a" />
    <path
      d="M4.6 8.2l2.2 2.2 4.6-4.8"
      fill="none"
      stroke="#ffffff"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CardIcon = () => (
  <svg
    width="18"
    height="14"
    viewBox="0 0 18 14"
    aria-hidden="true"
    style={{ flex: "none", marginRight: 8 }}
  >
    <rect x="0.7" y="0.7" width="16.6" height="12.6" rx="2" fill="#8a94a6" />
    <rect x="0.7" y="3.4" width="16.6" height="2.4" fill="#53627c" />
  </svg>
);

const Heading = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #0a1f44;
  text-align: center;
  margin: 10px 0 14px;
`;

const Toggle = styled.div`
  display: flex;
  width: fit-content;
  max-width: 100%;
  margin: 0 auto 16px;
  border: 1px solid #c9d3e0;
  border-radius: 8px;
  padding: 3px;
  background: #ffffff;
`;

const ToggleOption: any = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 0 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${(p: any) => (p.selected ? 700 : 500)};
  white-space: nowrap;
  background: ${(p: any) => (p.selected ? MEMBERSHIP_BLUE.normal : "transparent")};
  color: ${(p: any) => (p.selected ? "#ffffff" : "#53627c")};
`;

const Saving = styled.span`
  font-size: 11px;
  font-weight: 600;
  margin-left: 6px;
  opacity: 0.85;
`;

const PlanCard = styled.div`
  border: 3px solid #0a1f44;
  border-radius: 14px;
  padding: 18px 18px 14px;
  margin-bottom: 16px;
`;

const PlanName = styled.div`
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  color: #0a1f44;
`;

const AfterTrial = styled.div`
  font-size: 14px;
  color: #0a1f44;
  margin-top: 6px;
`;

const Price = styled.div`
  font-size: 27px;
  font-weight: 600;
  color: #0a1f44;
  line-height: 1.25;
  margin-bottom: 12px;
`;

const PriceSuffix = styled.span`
  display: block;
  font-size: 13px;
  font-weight: 400;
  color: #8a94a6;
`;

const Benefits = styled.div`
  padding-top: 2px;
`;

const Benefit = styled.div`
  display: flex;
  align-items: flex-start;
  font-size: 13px;
  color: #0a1f44;
  line-height: 20px;
  margin-bottom: 4px;
`;


const ExpandButton = styled.button`
  background: none;
  border: none;
  padding: 8px 0 4px;
  color: #0584d7;
  font-size: 13px;
  cursor: pointer;
`;

const CcBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #f5f6f8;
  font-size: 12px;
  color: #364866;
`;


const PrimaryButton = styled.button`
  width: 100%;
  ${membershipPrimary}
  padding: 14px 0;
  min-height: 48px;
`;

const TextButton = styled.button`
  display: block;
  width: 100%;
  background: none;
  border: none;
  color: #0f9bf0;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 0;
  cursor: pointer;
  min-height: 44px;

  &:disabled {
    color: #8a94a6;
    cursor: default;
  }
`;

export default PaywallTrialOffer;
